import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';

/**
 * Service to track navigation history and handle catalog-specific navigation.
 * Used to properly handle browser back button when catalog toolbar navigation is active.
 */
@Injectable({ providedIn: 'root' })
export class RouterHistoryService {
  private router = inject(Router);

  private urlHistory: string[] = [];
  private currentIndex = -1;
  private previousUrl: string | undefined = undefined;
  private currentUrl: string | undefined = undefined;
  private toolbarNavigationActive = false;
  private toolbarNavigationCount = 0;
  private canGoBackSubject = new BehaviorSubject<boolean>(false);
  public canGoBack$ = this.canGoBackSubject.asObservable();

  constructor() {
    this.currentUrl = this.router.url;
    // Initialize history
    this.urlHistory.push(this.currentUrl);
    this.currentIndex = 0;

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const newUrl = event.urlAfterRedirects;

      // Check if the base route changed (not just query params)
      const currentBase = this.currentUrl?.split('?')[0] || '';
      const newBase = newUrl.split('?')[0];
      const baseRouteChanged = currentBase !== newBase;

      // If toolbar navigation is active but base route changed,
      // it means user navigated away from catalog normally (e.g., clicked header link)
      // Reset toolbar navigation flag and update history normally
      if (this.toolbarNavigationActive && baseRouteChanged) {
        this.toolbarNavigationActive = false;
        this.toolbarNavigationCount = 0;
      }

      // Only update history if not in catalog toolbar navigation mode
      if (!this.toolbarNavigationActive) {
        // Check if we went back - look for newUrl in history before current position
        const backIndex = this.urlHistory.lastIndexOf(newUrl, this.currentIndex - 1);

        if (backIndex !== -1) {
          this.currentIndex = backIndex;
          this.previousUrl = this.currentIndex > 0 ? this.urlHistory[this.currentIndex - 1] : undefined;
        } else {
          // Forward navigation - add to history
          // Remove any forward history if we navigated from middle of stack
          this.urlHistory = this.urlHistory.slice(0, this.currentIndex + 1);

          this.previousUrl = this.currentUrl;
          this.urlHistory.push(newUrl);
          this.currentIndex = this.urlHistory.length - 1;
        }
      }

      this.currentUrl = newUrl;

      this.updateCanGoBack();
    });

    // Set initial canGoBack state
    this.updateCanGoBack();
  }

  public getPreviousUrl(): string | undefined {
    return this.previousUrl;
  }

  public getCurrentUrl(): string | undefined {
    return this.currentUrl;
  }

  /**
   * Set whether catalog toolbar navigation is active.
   * When active, URL changes won't update previousUrl.
   */
  public setToolbarNavigationActive(active: boolean): void {
    this.toolbarNavigationActive = active;
    if (!active) {
      this.toolbarNavigationCount = 0;
    }
    this.updateCanGoBack();
  }

  public isToolbarNavigationActive(): boolean {
    return this.toolbarNavigationActive;
  }

  public setToolbarNavigationCount(count: number): void {
    this.toolbarNavigationCount = count;
    this.updateCanGoBack();
  }

  public getToolbarNavigationCount(): number {
    return this.toolbarNavigationCount;
  }

  private updateCanGoBack(): void {
    const canGoBack = this.calculateCanGoBack();
    this.canGoBackSubject.next(canGoBack);
  }

  /**
   * Check if back navigation is possible within the application.
   * Returns true if there's a previous URL that belongs to this app.
   */
  public canGoBack(): boolean {
    return this.canGoBackSubject.value;
  }

  private calculateCanGoBack(): boolean {
    const currentBase = this.currentUrl?.split('?')[0] || '';

    // Look back through the whole stack, not just the immediate previous entry,
    // since query-param-only navigations (e.g. toggling a filter) keep the same base route
    // and would otherwise reset previousUrl to another entry with that same base.
    for (let i = this.currentIndex - 1; i >= 0; i--) {
      const url = this.urlHistory[i];
      if (url.startsWith('/') && url.split('?')[0] !== currentBase) {
        return true;
      }
    }

    return false;
  }
}
